import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth';

const RESEND_COOLDOWN_SECONDS = 30;

function formatMMSS(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function secondsUntil(iso: string | null): number | null {
  if (!iso) return null;
  return Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 1000));
}

/**
 * Dedicated email-verification step. The register flow lands here (not the
 * dashboard): the seller confirms the 6-digit code we emailed, and only then
 * are they moved into the dashboard. Reuses the same endpoints as the old
 * dashboard banner (/me/verify-email, /me/resend-verification).
 */
export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation() as { state: { emailSent?: boolean } | null };
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const clear = useAuthStore((s) => s.clear);

  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  // When register couldn't send the code, it passes emailSent:false so we can
  // tell the seller to resend rather than wait on a code that never arrives.
  const [sendFailed, setSendFailed] = useState(location.state?.emailSent === false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Guards: no session -> login; already verified -> straight to dashboard.
  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    if (user.emailVerified) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  // Learn the active code's real expiry so the countdown survives a reload.
  useEffect(() => {
    if (!user || user.emailVerified) return;
    api.get('/me/verify-email')
      .then(({ data }) => setSecondsLeft(secondsUntil(data.codeExpiresAt)))
      .catch(() => {});
    inputRef.current?.focus();
  }, [user?.id, user?.emailVerified]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (secondsLeft === null || secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((s) => (s !== null ? s - 1 : s)), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  if (!user || user.emailVerified) return null;

  const expired = secondsLeft === 0;

  async function submitCode() {
    if (code.length !== 6) return;
    setVerifying(true);
    try {
      const { data } = await api.post('/me/verify-email', { code });
      setUser(data.user);
      toast.success('Email verified. Welcome to Souqli.');
      navigate('/dashboard', { replace: true });
    } catch (e: any) {
      const status = e?.response?.status;
      toast.error(
        status === 429 ? 'Too many attempts. Please wait a minute and try again.'
          : e?.response?.data?.message ?? 'That code is not right. Check it and try again.',
      );
    } finally {
      setVerifying(false);
    }
  }

  async function resend() {
    setResending(true);
    try {
      const { data } = await api.post('/me/resend-verification');
      toast.success('A new code is on its way.');
      setSendFailed(false);
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setSecondsLeft(secondsUntil(data.expiresAt));
      inputRef.current?.focus();
    } catch (e: any) {
      const status = e?.response?.status;
      toast.error(
        status === 429 ? 'Too many attempts. Please wait a few minutes and try again.'
          : e?.response?.data?.message ?? 'Could not send the code just now. Try again in a moment.',
      );
    } finally {
      setResending(false);
    }
  }

  function useDifferentEmail() {
    clear();
    navigate('/register', { replace: true });
  }

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      {/* Top bar: the same Souqli logo the homepage header uses. */}
      <div className="border-b border-warmline">
        <div className="max-w-[1100px] mx-auto px-5 md:px-8 h-14 flex items-center">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" aria-label="Souqli - home">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-ink shadow-glow overflow-hidden">
              <img src="/souqli-mark.png" alt="" className="w-[26px] h-[26px] object-contain" />
            </div>
            <div className="display text-ink text-xl leading-none">Souqli</div>
          </Link>
        </div>
      </div>

      <div className="flex-1 grid place-items-center px-5 py-10">
        <div className="w-full max-w-[440px]">
          <div className="text-gold-dark text-[11px] font-bold uppercase tracking-[0.18em] mb-2">One last step</div>
          <h1 className="display text-3xl md:text-[34px] leading-tight">Verify your email.</h1>
          <p className="text-mute text-sm mt-2 leading-relaxed">
            We sent a 6-digit code to <span className="text-ink font-medium">{user.email}</span>. Enter it below to finish setting up your account.
          </p>

          {sendFailed && (
            <div className="mt-4 rounded-luxe border border-warn/40 bg-warn/10 px-4 py-3 text-[13px] text-ink">
              We could not send your code automatically. Tap <span className="font-semibold">Resend code</span> below to try again.
            </div>
          )}

          <div className="card p-5 md:p-6 mt-5">
            <label className="label" htmlFor="verify-code">Confirmation code</label>
            <input
              id="verify-code"
              ref={inputRef}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => { if (e.key === 'Enter') submitCode(); }}
              placeholder="123456"
              inputMode="numeric"
              autoComplete="one-time-code"
              className="input input-lg mono text-center text-2xl tracking-[0.4em]"
            />
            <div className="mt-1.5 text-[11px] text-mute h-4">
              {expired
                ? <span className="text-bad font-medium">Code expired - resend to get a new one.</span>
                : secondsLeft != null ? <span>Code expires in {formatMMSS(secondsLeft)}.</span> : null}
            </div>

            <button
              type="button"
              onClick={submitCode}
              disabled={code.length !== 6 || verifying || expired}
              className="btn btn-primary w-full mt-3"
            >
              {verifying ? 'Verifying…' : 'Verify & continue'}
            </button>

            <div className="flex items-center justify-between mt-4 text-[13px]">
              <span className="text-mute">Didn't get it?</span>
              <button
                type="button"
                onClick={resend}
                disabled={resending || cooldown > 0}
                className="font-semibold text-gold-dark hover:underline disabled:text-mute disabled:no-underline"
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : resending ? 'Sending…' : 'Resend code'}
              </button>
            </div>
          </div>

          <button type="button" onClick={useDifferentEmail} className="block mx-auto mt-5 text-[12px] text-mute hover:text-ink transition">
            Wrong email? Start over
          </button>
        </div>
      </div>
    </div>
  );
}
